import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Header } from '../components/ui/header';
import { Footer } from '../components/ui/footer';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { CheckCircle, Download, MessageCircle } from 'lucide-react';

export const ThankYou = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const requestType = params.get('type');
  const brochureUrl = params.get('brochure');

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToTop();
  }, []);

  const handleBackHome = () => {
    navigate('/');
  };

  const handleChatNow = () => {
    window.open('https://brochure.needahairmakeoverfranchise.co.uk', '_blank');
  };

  const handleDownloadBrochure = () => {
    if (brochureUrl) {
      window.open(brochureUrl, '_blank');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <SEO page="thankYou" />
      <Header onApplyClick={handleBackHome} />
      
      <div className="pt-20 pb-16">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto text-center">
            
            {/* Success Icon */}
            <div className="mb-8">
              <CheckCircle className="w-24 h-24 text-accent-primary mx-auto mb-4" />
              <h1 className="text-4xl md:text-5xl font-bold text-dark mb-4">
                Thank You!
              </h1>
            </div>

            {/* Success Message */}
            <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
              {requestType === 'brochure' ? (
                <>
                  <h2 className="text-2xl font-bold text-dark mb-4">
                    🎉 Your Franchise Brochure is Ready!
                  </h2>
                  <p className="text-lg text-subtle mb-6">
                    Thank you for your interest in our franchise opportunity. Your brochure download is ready below.
                  </p>
                  
                  {brochureUrl && (
                    <Button
                      onClick={handleDownloadBrochure}
                      className="bg-accent-primary hover:bg-accent-primary/90 text-white px-8 py-4 rounded-xl text-lg font-semibold mb-4"
                    >
                      <Download className="w-5 h-5 mr-2" />
                      📄 Download Your Brochure
                    </Button>
                  )}
                </>
              ) : (
                <>
                  <h2 className="text-2xl font-bold text-dark mb-4">
                    ✅ Application Received!
                  </h2>
                  <p className="text-lg text-subtle mb-6">
                    Thank you for your franchise application. We've received your details and will contact you within 24 hours.
                  </p>
                </>
              )}

              {/* Chat CTA */}
              <div className="bg-accent-primary/10 rounded-xl p-6 mt-6">
                <h3 className="text-xl font-bold text-dark mb-3">
                  Have Questions? Let's Chat!
                </h3>
                <p className="text-subtle mb-4">
                  Our franchise experts are ready to answer any questions about territories, investment, or the application process.
                </p>
                <Button
                  onClick={handleChatNow}
                  variant="outline"
                  className="border-2 border-accent-primary text-accent-primary hover:bg-accent-primary hover:text-white"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  💬 Chat with Our Experts
                </Button>
              </div>
            </div>

            {/* Next Steps */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h3 className="text-2xl font-bold text-dark mb-6">What Happens Next?</h3>
              
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-accent-primary/20 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-accent-primary font-bold text-xl">1</span>
                  </div>
                  <h4 className="font-semibold text-dark mb-2">Review & Contact</h4>
                  <p className="text-sm text-subtle">We'll review your application and contact you within 24 hours</p>
                </div>
                
                <div className="text-center">
                  <div className="w-12 h-12 bg-accent-primary/20 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-accent-primary font-bold text-xl">2</span>
                  </div>
                  <h4 className="font-semibold text-dark mb-2">Territory Discussion</h4>
                  <p className="text-sm text-subtle">We'll discuss available territories and investment details</p>
                </div>
                
                <div className="text-center">
                  <div className="w-12 h-12 bg-accent-primary/20 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-accent-primary font-bold text-xl">3</span>
                  </div>
                  <h4 className="font-semibold text-dark mb-2">Get Started</h4>
                  <p className="text-sm text-subtle">Begin your journey as a franchise partner</p>
                </div>
              </div>
            </div>

            {/* Back to Home */}
            <div className="mt-8">
              <Button
                onClick={handleBackHome}
                variant="outline"
                className="border-2 border-gray-300 text-gray-600 hover:border-accent-primary hover:text-accent-primary"
              >
                ← Back to Homepage
              </Button>
            </div>

          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};